import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { EventId } from '@store/models/ids';
import type { SpaceWithAvailability } from '@store/models/spaces';
import type { BookingFormState, BookingDraft, SpaceDetails } from '@store/models/booking';

const initialState: BookingFormState = {
  activeDraft: null,
  eventFormData: {},
  eventId: null,
  etag: null,
  requestId: null,
  selectedSpace: null,
  availableSpaces: [],
  spacesLoading: false,
  spacesLoadingMore: false,
  spacesContinuationToken: null,
  spacesHasMore: false,
  spacesBatchError: null,
  dirtySteps: {},
  validationErrors: {},
  submissionStatus: 'idle',
  apiError: null,
  editingFromReview: false,
};

const EMPTY_VALIDATION_ERRORS: Record<string, string> = {};

export const bookingFormSlice = createSlice({
  name: 'bookingForm',
  initialState,
  reducers: {
    createDraft: (state, _action: PayloadAction<{ formId: string }>) => {
      const now = new Date().toISOString();
      state.activeDraft = {
        draftId: `draft-${Date.now()}`,
        data: {},
        currentStepIndex: 0,
        highestVisitedStep: 0,
        createdAt: now,
        lastModifiedAt: now,
        status: 'draft',
      };
      state.eventFormData = {};
      state.eventId = null;
      state.etag = null;
      state.requestId = null;
      state.selectedSpace = null;
      state.availableSpaces = [];
      state.spacesLoading = false;
      state.spacesLoadingMore = false;
      state.spacesContinuationToken = null;
      state.spacesHasMore = false;
      state.spacesBatchError = null;
      state.dirtySteps = {};
      state.validationErrors = {};
      state.submissionStatus = 'idle';
      state.apiError = null;
      state.editingFromReview = false;
    },

    loadDraft: (state, action: PayloadAction<BookingDraft>) => {
      state.activeDraft = action.payload;
      state.eventFormData = { ...action.payload.data };
      state.submissionStatus = 'idle';
      state.apiError = null;
      const selectedSpace = action.payload.data.selectedSpace as SpaceDetails | undefined;
      state.selectedSpace = selectedSpace ?? null;
    },

    updateStepData: (
      state,
      action: PayloadAction<{ stepIndex: number; data: Record<string, unknown> }>
    ) => {
      if (!state.activeDraft) return;
      state.eventFormData = { ...state.eventFormData, ...action.payload.data };
      state.activeDraft.data = { ...state.eventFormData };
      state.dirtySteps[action.payload.stepIndex] = true;
      state.activeDraft.lastModifiedAt = new Date().toISOString();
    },

    commitStep: (state, action: PayloadAction<{ stepIndex: number }>) => {
      if (!state.activeDraft) return;
      const { stepIndex } = action.payload;
      state.activeDraft.highestVisitedStep = Math.max(
        state.activeDraft.highestVisitedStep,
        stepIndex
      );
      state.dirtySteps[stepIndex] = false;
      delete state.validationErrors[stepIndex];
    },

    selectSpace: (state, action: PayloadAction<SpaceDetails | null>) => {
      state.selectedSpace = action.payload;
      state.eventFormData.selectedSpace = action.payload;
      if (state.activeDraft) {
        state.activeDraft.data = { ...state.eventFormData };
        state.activeDraft.lastModifiedAt = new Date().toISOString();
      }
    },

    goToStep: (state, action: PayloadAction<{ stepIndex: number; forceReview?: boolean }>) => {
      if (!state.activeDraft) return;
      if (action.payload.forceReview && action.payload.stepIndex === 4) {
        state.activeDraft.currentStepIndex = action.payload.stepIndex;
        return;
      }
      const maxAllowed = state.activeDraft.highestVisitedStep + 1;
      if (action.payload.stepIndex <= maxAllowed) {
        state.activeDraft.currentStepIndex = action.payload.stepIndex;
      }
    },

    setEditingFromReview: (state, action: PayloadAction<boolean>) => {
      state.editingFromReview = action.payload;
    },

    saveAsDraft: (state) => {
      if (!state.activeDraft) return;
      state.activeDraft.lastModifiedAt = new Date().toISOString();
      state.dirtySteps = {};
    },

    discardDraft: (state) => {
      state.activeDraft = null;
      state.eventFormData = {};
      state.eventId = null;
      state.etag = null;
      state.requestId = null;
      state.selectedSpace = null;
      state.availableSpaces = [];
      state.spacesLoading = false;
      state.spacesLoadingMore = false;
      state.spacesContinuationToken = null;
      state.spacesHasMore = false;
      state.spacesBatchError = null;
      state.dirtySteps = {};
      state.validationErrors = {};
      state.submissionStatus = 'idle';
      state.apiError = null;
      state.editingFromReview = false;
    },

    submitForm: (state) => {
      if (!state.activeDraft) return;
      state.submissionStatus = 'submitting';
      state.validationErrors = {};
      state.apiError = null;
    },

    setEventId: (state, action: PayloadAction<EventId | null>) => {
      state.eventId = action.payload;
    },

    setEtag: (state, action: PayloadAction<string | null>) => {
      state.etag = action.payload;
    },

    setRequestId: (state, action: PayloadAction<string | null>) => {
      state.requestId = action.payload;
    },

    setAvailableSpaces: (state, action: PayloadAction<SpaceWithAvailability[]>) => {
      state.availableSpaces = action.payload;
    },

    setSpacesLoading: (state, action: PayloadAction<boolean>) => {
      state.spacesLoading = action.payload;
    },

    setApiError: (state, action: PayloadAction<string | null>) => {
      state.apiError = action.payload;
    },

    setSpacesContinuationToken: (state, action: PayloadAction<string | null>) => {
      state.spacesContinuationToken = action.payload;
    },

    setSpacesHasMore: (state, action: PayloadAction<boolean>) => {
      state.spacesHasMore = action.payload;
    },

    appendAvailableSpaces: (state, action: PayloadAction<SpaceWithAvailability[]>) => {
      state.availableSpaces = [...state.availableSpaces, ...action.payload];
    },

    setSpacesLoadingMore: (state, action: PayloadAction<boolean>) => {
      state.spacesLoadingMore = action.payload;
    },

    setSpacesBatchError: (state, action: PayloadAction<string | null>) => {
      state.spacesBatchError = action.payload;
    },

    resetSpacesPagination: (state) => {
      state.availableSpaces = [];
      state.spacesContinuationToken = null;
      state.spacesHasMore = false;
      state.spacesBatchError = null;
      state.spacesLoadingMore = false;
      state.selectedSpace = null;
      if (state.activeDraft) {
        state.eventFormData.selectedSpace = null;
        state.activeDraft.data = { ...state.eventFormData };
      }
    },

    setSubmissionStatus: (
      state,
      action: PayloadAction<'idle' | 'submitting' | 'submitted' | 'error'>
    ) => {
      state.submissionStatus = action.payload;
      if (action.payload === 'submitted' && state.activeDraft) {
        state.activeDraft.status = 'submitted';
        state.activeDraft.lastModifiedAt = new Date().toISOString();
        state.dirtySteps = {};
      }
    },

    setValidationErrors: (
      state,
      action: PayloadAction<{ stepIndex: number; errors: Record<string, string> }>
    ) => {
      state.validationErrors[action.payload.stepIndex] = action.payload.errors;
    },

    clearValidationErrors: (state, action: PayloadAction<{ stepIndex?: number }>) => {
      if (action.payload.stepIndex !== undefined) {
        delete state.validationErrors[action.payload.stepIndex];
      } else {
        state.validationErrors = {};
      }
    },
  },
});

export const {
  createDraft,
  loadDraft,
  updateStepData,
  commitStep,
  selectSpace,
  goToStep,
  setEditingFromReview,
  saveAsDraft,
  discardDraft,
  submitForm,
  setEventId,
  setEtag,
  setRequestId,
  setAvailableSpaces,
  setSpacesLoading,
  setApiError,
  setSpacesContinuationToken,
  setSpacesHasMore,
  appendAvailableSpaces,
  setSpacesLoadingMore,
  setSpacesBatchError,
  resetSpacesPagination,
  setSubmissionStatus,
  setValidationErrors,
  clearValidationErrors,
} = bookingFormSlice.actions;

// Selectors
export const selectActiveDraft = (state: { bookingForm: BookingFormState }) =>
  state.bookingForm.activeDraft;

export const selectEventId = (state: { bookingForm: BookingFormState }) =>
  state.bookingForm.eventId;

export const selectEtag = (state: { bookingForm: BookingFormState }) => state.bookingForm.etag;

export const selectRequestId = (state: { bookingForm: BookingFormState }) =>
  state.bookingForm.requestId;

export const selectSelectedSpace = (state: { bookingForm: BookingFormState }) =>
  state.bookingForm.selectedSpace;

export const selectAvailableSpaces = (state: { bookingForm: BookingFormState }) =>
  state.bookingForm.availableSpaces;

export const selectSpacesLoading = (state: { bookingForm: BookingFormState }) =>
  state.bookingForm.spacesLoading;

export const selectCurrentStepIndex = (state: { bookingForm: BookingFormState }) =>
  state.bookingForm.activeDraft?.currentStepIndex ?? 0;

export const selectHighestVisitedStep = (state: { bookingForm: BookingFormState }) =>
  state.bookingForm.activeDraft?.highestVisitedStep ?? 0;

export const selectFormData = (state: { bookingForm: BookingFormState }) =>
  state.bookingForm.eventFormData;

export const selectIsStepDirty =
  (stepIndex: number) => (state: { bookingForm: BookingFormState }) =>
    state.bookingForm.dirtySteps[stepIndex] ?? false;

export const selectStepValidationErrors =
  (stepIndex: number) => (state: { bookingForm: BookingFormState }) =>
    state.bookingForm.validationErrors[stepIndex] ?? EMPTY_VALIDATION_ERRORS;

export const selectSubmissionStatus = (state: { bookingForm: BookingFormState }) =>
  state.bookingForm.submissionStatus;

export const selectHasUnsavedChanges = (state: { bookingForm: BookingFormState }) =>
  Object.values(state.bookingForm.dirtySteps).some(Boolean);

export const selectApiError = (state: { bookingForm: BookingFormState }) =>
  state.bookingForm.apiError;

export const selectEditingFromReview = (state: { bookingForm: BookingFormState }) =>
  state.bookingForm.editingFromReview;

export const selectSpacesContinuationToken = (state: { bookingForm: BookingFormState }) =>
  state.bookingForm.spacesContinuationToken;

export const selectSpacesHasMore = (state: { bookingForm: BookingFormState }) =>
  state.bookingForm.spacesHasMore;

export const selectSpacesLoadingMore = (state: { bookingForm: BookingFormState }) =>
  state.bookingForm.spacesLoadingMore;

export const selectSpacesBatchError = (state: { bookingForm: BookingFormState }) =>
  state.bookingForm.spacesBatchError;

export default bookingFormSlice.reducer;
